import { CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { SimpleDocCommand } from "./types";
import { createFileLogger } from '../../../../utils/logger';
import { entityResolver } from '../../../../services/entityResolver';
import { createDocumentReader } from "../../services/doc/DocumentReader";

const log = createFileLogger('DOC_READ_V2');

export const docReadV2Command: SimpleDocCommand = {
  id: 'doc_read_v2',
  description: `Read content from Documents and PDFs using flexible selectors.

**SUPPORTED ENTITIES:**
- Documents (doc_*)
- PDFs (pdf_* or file_*)
- For Notes/Sheets/Canvas/Slides, use entity_read instead

Max 3000 words per read. For large documents, read in chunks using indexRange.

**SELECTORS (selector parameter):**

"all" - Entire document
{"indexRange": {"from": 0, "to": 10}} - Nodes 0-10 by position
{"indexRange": {"start": 0, "count": 10}} - 10 nodes from position 0
{"range": {"from": "node_abc_123", "to": "node_xyz_789"}} - Between two node IDs
{"range": {"start": "node_abc_123", "count": 10}} - 10 nodes from node ID
{"query": "search text"} - Text search

**EXAMPLES:**

Read entire document:
{ "id": "doc_large_pig_coconut", "selector": "all" }

Read first 10 nodes:
{ "id": "doc_large_pig_coconut", "selector": "{\"indexRange\": {\"from\": 0, \"to\": 10}}" }

Search for text:
{ "id": "doc_large_pig_coconut", "selector": "{\"query\": \"introduction\"}" }`,
  logicalName: 'doc_read',
  version: 'v2',
  appType: 'docs',
  // No outputStrategy needed - summarizeToolResult.ts has custom handling for doc_read_v2 (lines 109-143)
  
  // ✅ NEW: Structured view data for guaranteed persistence
  formatViewData: (result: any, toolInput: any) => {
    try {
      if (result?.data?.type === 'markdown_content') {
        const data = result.data;
        return {
          type: 'markdown_content',
          summary: result.message || result.summary || 'Document content',
          data: {
            markdown: data.markdown?.substring(0, 1000) + (data.markdown?.length > 1000 ? '\n\n... [truncated]' : ''),
            nodeCount: data.node_count || data.available_node_ids?.length || 0,
            documentName: data.metadata?.documentName || toolInput?.id || 'document',
            structuralInfo: data.structuralInfo
          },
          meta: {
            duration: result.metadata?.executionTime
          }
        };
      }
      
      // Fallback
      const docName = toolInput?.id || 'document';
      const nodeCount = result?.data?.node_count || result?.data?.nodeList?.length;
      let summary = `Read ${docName}`;
      if (nodeCount) {
        summary += ` (${nodeCount} sections)`;
      }
      
      return {
        type: 'tool_summary',
        summary,
        data: result?.data || {}
      };
      
    } catch (error) {
      console.warn('[DOC_READ_V2] Error in formatViewData:', error);
      return {
        type: 'tool_summary',
        summary: 'Read document',
        data: {}
      };
    }
  },
  
  // ✅ ENHANCED: Custom result formatter with rich preview support (DEPRECATED - use formatViewData)
  formatResultPreview: (result: any, toolInput: any) => {
    try {
      // For expandable view, return structured preview object with markdown content
      if (result?.data?.type === 'markdown_content') {
        const data = result.data;
        const preview = {
          type: 'markdown_content',
          summary: result.message || result.summary || 'Document content',
          content: {
            markdown: data.markdown?.substring(0, 1000) + (data.markdown?.length > 1000 ? '\n\n... [truncated]' : ''),
            nodeCount: data.node_count || data.available_node_ids?.length || 0,
            documentName: data.metadata?.documentName || toolInput?.id || 'document',
            structuralInfo: data.structuralInfo
          }
        };
        
        // Return structured object for expandable view
        return preview;
      }
      
      // For non-expandable view, return simple string
      // Prefer StandardToolResult.message first
      if (result && typeof result.message === 'string' && result.message.length > 0) {
        return result.message;
      }
      
      // Then try summary field  
      if (result && typeof result.summary === 'string' && result.summary.length > 0) {
        return result.summary;
      }
      
      // Try data.type for operation description
      if (result?.data?.type) {
        const docName = result?.data?.metadata?.documentName || toolInput?.id || 'document';
        const nodeCount = result?.data?.node_count || result?.data?.nodeList?.length;
        
        let message = `Read ${docName}`;
        if (nodeCount) {
          message += ` (${nodeCount} sections)`;
        }
        return message;
      }
      
      // Fallback
      const docName = toolInput?.id || 'document';
      return `Read ${docName}`;
      
    } catch (error) {
      console.warn('[DOC_READ_V2_FORMATTER] Error in formatResultPreview:', error);
      return 'Read document';
    }
  },
  
  schema: {
    parameters: [
      {
        name: 'id',
        type: 'string',
        description: 'Document ID (doc_*, pdf_*, or file_*)',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Document ID cannot be empty';
          if (value.startsWith('note_') || value.startsWith('sheet_') || value.startsWith('canvas_') || value.startsWith('slide_') || value.startsWith('presentation_')) {
            return 'doc_read_v2 is for Documents and PDFs only. Use entity_read for v2 entities (note_*, sheet_*, canvas_*, slide_*).';
          }
          return null;
        }
      },
      {
        name: 'selector',
        type: 'string',
        description: 'What to read. See SELECTORS section. Defaults to "all".',
        required: false,
        defaultValue: 'all'
      }
    ]
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    
    /**
     * Truncate nodes to fit within word limit
     * Returns truncated nodes + metadata about what was cut
     */
    function truncateToWordLimit(
      nodes: any[],
      maxWords: number,
      context: { selectorType: string; searchQuery?: string; fromIndex?: number; toIndex?: number }
    ): {
      truncatedNodes: any[];
      totalWords: number;
      truncated: boolean;
      originalNodeCount: number;
      originalWordCount: number;
      truncationMessage?: string;
    } {
      let currentWords = 0;
      const truncatedNodes: any[] = [];
      
      // Calculate total word count first
      const originalWordCount = nodes.reduce((sum, n) => 
        sum + ((n.wordCount !== undefined ? n.wordCount : (n.text || '').split(/\s+/).filter((w: string) => w.trim()).length)), 0
      );
      
      // If already within limit, return as-is
      if (originalWordCount <= maxWords) {
        return {
          truncatedNodes: nodes,
          totalWords: originalWordCount,
          truncated: false,
          originalNodeCount: nodes.length,
          originalWordCount
        };
      }
      
      // Truncate to fit limit
      for (const node of nodes) {
        const nodeWords = node.wordCount !== undefined 
          ? node.wordCount 
          : (node.text || '').split(/\s+/).filter((w: string) => w.trim()).length;
        
        if (currentWords + nodeWords <= maxWords) {
          truncatedNodes.push(node);
          currentWords += nodeWords;
        } else {
          // Exceeded limit - stop here
          break;
        }
      }
      
      const remainingNodes = nodes.length - truncatedNodes.length;
      const remainingWords = originalWordCount - currentWords;
      
      // Build truncation message based on selector type
      let truncationMessage;
      if (context.selectorType === 'query' && context.searchQuery) {
        truncationMessage = `Found ${nodes.length} nodes containing "${context.searchQuery}" (${originalWordCount.toLocaleString()} words total). Showing FIRST ${currentWords.toLocaleString()} words (${truncatedNodes.length} nodes). Remaining: ${remainingNodes} nodes (${remainingWords.toLocaleString()} words) not shown. Use indexRange with specific positions to read more sections.`;
      } else if (context.selectorType === 'indexRange') {
        truncationMessage = `Content truncated: Index range ${context.fromIndex}-${context.toIndex} contains ${originalWordCount.toLocaleString()} words. Showing FIRST ${currentWords.toLocaleString()} words (${truncatedNodes.length} nodes). Remaining: ${remainingNodes} nodes (${remainingWords.toLocaleString()} words) not shown. Reduce range size or read in smaller chunks.`;
      } else if (context.selectorType === 'range') {
        truncationMessage = `Content truncated: Range contains ${originalWordCount.toLocaleString()} words. Showing FIRST ${currentWords.toLocaleString()} words (${truncatedNodes.length} nodes). Remaining: ${remainingNodes} nodes (${remainingWords.toLocaleString()} words) not shown. Use smaller range or indexRange to read specific sections.`;
      } else {
        // 'all' selector
        const nextStartIndex = truncatedNodes.length > 0 ? truncatedNodes.length : 0;
        truncationMessage = `Document truncated: Showing FIRST ${currentWords.toLocaleString()} words (${truncatedNodes.length} nodes) of ${originalWordCount.toLocaleString()} total words. Remaining: ${remainingNodes} nodes (${remainingWords.toLocaleString()} words) not shown. Use indexRange to read specific sections (e.g., {"indexRange": {"start": ${nextStartIndex}, "count": 50}}).`;
      }
      
      return {
        truncatedNodes,
        totalWords: currentWords,
        truncated: true,
        originalNodeCount: nodes.length,
        originalWordCount,
        truncationMessage
      };
    }
    
    const startTime = Date.now();
    
    try {
      // Emit loading/progress states for better UX
      try { context.onProgress?.({ type: 'streaming_update', activity: 'Reading document...' }); } catch {}
      const { id, selector = 'all' } = params;
      if (!id) {
        return createErrorResult('Missing document ID', 'id parameter is required', 'validation');
      }

      // Resolve the entity and check if it's a PDF
      let resolved: any;
      try {
        resolved = await entityResolver.resolveEntity(id, context);
      } catch (e) {
        // 🚨 FIXED: Check BOTH new entityReferences AND legacy entityRefs
        const entityReferences = (context as any)?.entityReferences || [];
        const windowContents = (context.spaceContext as any)?.entityRefs || {};
        
        // Get available documents from both systems
        const availableDocs = [
          // From new entityReferences system
          ...entityReferences.map((ref: any) => ({ 
            llmId: ref.llmId, 
            title: ref.title, 
            isActive: ref.isActive || false,
            source: 'entityReferences'
          })),
          // From legacy entityRefs system
          ...Object.keys(windowContents)
            .filter(key => key.startsWith('doc_'))
            .map(key => ({ 
              llmId: key, 
              title: (windowContents as any)[key]?.title || (windowContents as any)[key]?.entityName || 'Untitled', 
              isActive: (windowContents as any)[key]?.isActive || false,
              source: 'entityRefs'
            }))
        ];
        
        // Remove duplicates (prefer entityReferences)
        const uniqueDocs = availableDocs.reduce((acc: any[], doc) => {
          if (!acc.some(existing => existing.llmId === doc.llmId)) {
            acc.push(doc);
          }
          return acc;
        }, []);
        
        const availableLlmIdsList = uniqueDocs.map(doc => `- "${doc.llmId}" (${doc.title})${doc.isActive ? ' [ACTIVE]' : ''}`).join('\n');
        return createErrorResult('Document not found', `Document with llmId "${id}" not found in the current context.\n\n📋 AVAILABLE DOCUMENTS:\n${availableLlmIdsList}`, 'validation');
      }

      const documentName = resolved.entity.name || 'document';

      // 🚨 CRITICAL DEBUG: Log entity info to understand what we're getting
      log.log('[DOC_READ_V2] 🔍 Entity analysis:', {
        entityType: resolved.entity.entityType,
        name: resolved.entity.name,
        skeletonType: resolved.entity.skeleton?.['@type'],
        hasEnrichments: !!resolved.entity.skeleton?.enrichments,
        hasEnrichedData: !!resolved.entity.skeleton?.enrichedData,
        hasPdfAsDocument: !!resolved.entity.skeleton?.pdfAsDocument,
        pdfAsDocumentLength: resolved.entity.skeleton?.pdfAsDocument?.length || 0,
        skeletonKeys: Object.keys(resolved.entity.skeleton || {}),
        isPDFCheck1: resolved.entity.entityType?.includes('PDF'),
        isPDFCheck2: resolved.entity.entityType === 'File.Document.PDF',
        source: resolved.source,
        windowId: resolved.windowId
      });

      // 🚨 UNIFIED: Use DocumentReader abstraction for all document types
      // PDFs, Word docs, and Y.js documents all use the same interface
      log.log('[DOC_READ_V2] 🔧 Creating document reader (format-agnostic)');
      
      const { reader, error: readerError } = await createDocumentReader(id, context);
      if (readerError || !reader) {
        log.error('[DOC_READ_V2] Failed to create document reader:', {
          id,
          error: readerError || 'Reader creation failed'
        });
        return createErrorResult('Document not accessible', readerError || `Could not access document "${documentName}" (llmId: ${id}).`, 'system');
      }

      // Document reader created successfully
      log.log('[DOC_READ_V2] Document reader created:', {
        id,
        format: reader.format,
        documentName: reader.documentName
      });

      try { context.onProgress?.({ type: 'streaming_update', activity: 'Reading content...' }); } catch {}
      
      // Handle different selector types
      let result: any;
      let selectorMode = 'all';
      
      // Parse JSON selectors
      let parsedSelector: any = selector;
      
      // Handle selector passed as object (Claude might pass it directly instead of JSON string)
      if (typeof selector === 'object' && selector !== null && selector !== 'all') {
        parsedSelector = selector;
        log.log('[doc_read_v2] Selector provided as object:', parsedSelector);
      } else if (typeof selector === 'string' && selector !== 'all' && (selector.startsWith('{') || selector.startsWith('['))) {
        try {
          parsedSelector = JSON.parse(selector);
          log.log('[doc_read_v2] Parsed JSON selector from string:', parsedSelector);
        } catch (e) {
          log.warn('[doc_read_v2] Failed to parse selector as JSON, treating as string:', selector);
        }
      }
      
      // Handle indexRange selector (read nodes by document position)
      if (typeof parsedSelector === 'object' && parsedSelector.indexRange) {
        selectorMode = 'indexRange';
        
        const indexRangeParam = parsedSelector.indexRange;
        let fromIndex: number;
        let toIndex: number;
        
        // Support two formats: {"from": 50, "to": 100} OR {"start": 50, "count": 20}
        if ('from' in indexRangeParam && 'to' in indexRangeParam) {
          fromIndex = indexRangeParam.from;
          toIndex = indexRangeParam.to;
        } else if ('start' in indexRangeParam && 'count' in indexRangeParam) {
          fromIndex = indexRangeParam.start;
          toIndex = indexRangeParam.start + indexRangeParam.count - 1;
        } else {
          return createErrorResult(
            'Invalid indexRange',
            'indexRange requires either {"from": number, "to": number} or {"start": number, "count": number}',
            'validation'
          );
        }
        
        if (fromIndex < 0 || toIndex < fromIndex) {
          return createErrorResult(
            'Invalid indexRange',
            `Invalid range: from=${fromIndex}, to=${toIndex}. Ensure from >= 0 and to >= from.`,
            'validation'
          );
        }
        
        // Use reader (format-agnostic)
        const allContent = reader.readAll();
        const allNodes = allContent.nodes;
        
        if (toIndex >= allNodes.length) {
          return createErrorResult(
            'Index out of bounds',
            `Index ${toIndex} exceeds document length (${allNodes.length} nodes). Use indices 0-${allNodes.length - 1}.`,
            'validation'
          );
        }
        
        const rangeNodes = allNodes.slice(fromIndex, toIndex + 1);
        
        const enhancedNodes = rangeNodes.map((node: any, offset: number) => ({
          id: node.id,
          docIndex: fromIndex + offset,
          type: node.type,
          wordCount: (node.text || '').split(/\s+/).filter((w: string) => w.trim()).length,
          charCount: node.text?.length || 0,
          text: node.text
        }));
        
        // Apply truncation to fit within 3k word limit
        const MAX_READ_WORDS = 3000;
        const truncationResult = truncateToWordLimit(enhancedNodes, MAX_READ_WORDS, {
          selectorType: 'indexRange',
          fromIndex,
          toIndex
        });
        
        const finalNodes = truncationResult.truncatedNodes;
        const rangeMarkdown = finalNodes.map((node: any) => {
          const wordCountStr = node.wordCount ? ` (${node.wordCount} words)` : '';
          return `<!-- ${node.id}${wordCountStr} -->\n${node.text || ''}`;
        }).join('\n\n');
        
        log.log('[DOC_READ_V2] 🔍 CRITICAL: indexRange markdown created:', {
          markdownLength: rangeMarkdown.length,
          markdownPreview: rangeMarkdown.substring(0, 300),
          nodeCount: enhancedNodes.length,
          fromIndex,
          toIndex
        });
        
        const resultMessage = truncationResult.truncated
          ? truncationResult.truncationMessage
          : `Read ${finalNodes.length} nodes from index ${fromIndex} to ${fromIndex + finalNodes.length - 1}`;
        
        const dataPayload = {
          type: 'index_range_content',
          markdown: rangeMarkdown,
          nodes: finalNodes,
          node_count: finalNodes.length,  // ✅ Already present
          available_node_ids: finalNodes.map(n => n.id),
          indexRangeInfo: {
            from: fromIndex,
            to: toIndex,
            count: finalNodes.length,
            totalDocumentNodes: allNodes.length
          },
          metadata: {
            documentName,
            selector: parsedSelector,
            executionTime: Date.now() - startTime
          }
        };
        
        log.log('[DOC_READ_V2] 🔍 CRITICAL: indexRange data payload:', {
          dataKeys: Object.keys(dataPayload),
          type: dataPayload.type,
          hasMarkdown: !!dataPayload.markdown,
          markdownType: typeof dataPayload.markdown,
          markdownLength: dataPayload.markdown?.length
        });
        
        const result = createSuccessResult(
          resultMessage || `Read ${finalNodes.length} nodes`,
          dataPayload,
          {
            format: 'json',
            metadata: { 
              executionTime: Date.now() - startTime, 
              documentName, 
              affectedEntities: [resolved.windowId || reader.documentId], 
              method: 'doc_read_v2_indexRange' 
            } as any
          }
        );
        
        log.log('[DOC_READ_V2] 🔍 CRITICAL: indexRange StandardToolResult:', {
          status: result.status,
          message: result.message,
          hasData: !!result.data,
          dataKeys: result.data ? Object.keys(result.data) : [],
          dataHasMarkdown: !!result.data?.markdown,
          dataMarkdownLength: result.data?.markdown?.length
        });
        
        return result;
      }
      
      // Handle range selector (read nodes by node ID)
      if (typeof parsedSelector === 'object' && parsedSelector.range) {
        selectorMode = 'range';
        
        const rangeParam = parsedSelector.range;
        let fromNodeId: string;
        let toNodeId: string | undefined;
        
        // Support two formats: {"from": "node_id", "to": "node_id"} OR {"start": "node_id", "count": 20}
        if ('from' in rangeParam && 'to' in rangeParam) {
          fromNodeId = rangeParam.from;
          toNodeId = rangeParam.to;
        } else if ('start' in rangeParam && 'count' in rangeParam) {
          fromNodeId = rangeParam.start;
          // Will calculate end after finding start position
        } else {
          return createErrorResult(
            'Invalid range',
            'range requires either {"from": "node_id", "to": "node_id"} or {"start": "node_id", "count": number}',
            'validation'
          );
        }
        
        // Use reader (format-agnostic)
        const allContent = reader.readAll();
        const allNodes = allContent.nodes;
        
        const startIndex = allNodes.findIndex((n: any) => n.id === fromNodeId);
        if (startIndex === -1) {
          return createErrorResult(
            'Start node not found',
            `Node "${fromNodeId}" not found in document.`,
            'validation'
          );
        }
        
        let endIndex: number;
        
        if ('count' in rangeParam) {
          endIndex = Math.min(startIndex + rangeParam.count - 1, allNodes.length - 1);
        } else {
          endIndex = allNodes.findIndex((n: any) => n.id === toNodeId);
          if (endIndex === -1) {
            return createErrorResult(
              'End node not found',
              `Node "${toNodeId}" not found in document.`,
              'validation'
            );
          }
        }
        
        if (startIndex > endIndex) {
          return createErrorResult(
            'Invalid range',
            'Start node must come before end node in the document.',
            'validation'
          );
        }
        
        const rangeNodes = allNodes.slice(startIndex, endIndex + 1);
        
        // Enhance nodes with full metadata including document index
        const enhancedNodes = rangeNodes.map((node: any, offset: number) => ({
          id: node.id,
          docIndex: startIndex + offset,
          type: node.type,
          wordCount: (node.text || '').split(/\s+/).filter((w: string) => w.trim()).length,
          charCount: node.text?.length || 0,
          text: node.text
        }));
        
        // Apply truncation to fit within 3k word limit
        const MAX_READ_WORDS = 3000;
        const truncationResult = truncateToWordLimit(enhancedNodes, MAX_READ_WORDS, {
          selectorType: 'range'
        });
        
        const finalNodes = truncationResult.truncatedNodes;
        const rangeMarkdown = finalNodes.map((node: any) => {
          const text = node.text || '';
          const wordCountStr = node.wordCount ? ` (${node.wordCount} words)` : '';
          return `<!-- ${node.id}${wordCountStr} -->\n${text}`;
        }).join('\n\n');
        
        log.log('[doc_read_v2] Range processing:', {
          fromNodeId,
          toNodeId: toNodeId || 'calculated',
          totalNodes: enhancedNodes.length,
          totalWords: truncationResult.originalWordCount,
          truncated: truncationResult.truncated,
          returningNodes: finalNodes.length,
          returningWords: truncationResult.totalWords
        });
        
        const resultMessage = truncationResult.truncated
          ? truncationResult.truncationMessage
          : `Read ${finalNodes.length} nodes from "${documentName}"`;
        
        return createSuccessResult(
          resultMessage || `Read ${finalNodes.length} nodes`,
          {
            type: 'range_content',
            markdown: rangeMarkdown,
            nodes: finalNodes,
            node_count: finalNodes.length,
            available_node_ids: finalNodes.map((n: any) => n.id),
            rangeInfo: {
              startNodeId: fromNodeId,
              endNodeId: toNodeId || finalNodes[finalNodes.length - 1]?.id,
              startIndex,
              endIndex,
              nodeCount: finalNodes.length
            },
            metadata: {
              documentName,
              selector: parsedSelector,
              executionTime: Date.now() - startTime
            }
          },
          {
            format: 'json',
            metadata: { 
              executionTime: Date.now() - startTime, 
              documentName, 
              affectedEntities: [resolved.windowId || reader.documentId], 
              method: 'doc_read_v2_range' 
            } as any
          }
        );
      }
      
      // Handle query selector (search for nodes containing search term)
      if (typeof parsedSelector === 'object' && parsedSelector.query !== undefined) {
        selectorMode = 'search';
        
        const searchQuery = parsedSelector.query;
        
        // Validate query is not empty or whitespace-only
        if (typeof searchQuery !== 'string' || searchQuery.trim().length === 0) {
          return createErrorResult(
            'Invalid search query',
            'Query parameter cannot be empty or whitespace-only. Provide a search term (e.g., {"query": "methodology"})',
            'validation'
          );
        }
        
        // Use reader (format-agnostic) with fuzzy search enabled by default
        const searchResult = reader.search(searchQuery, { 
          fuzzy: true, 
          fuzzyThreshold: 0.8,
          limit: 999999
        });
        
        if (!searchResult.nodes || searchResult.nodes.length === 0) {
          return createErrorResult(
            'No matches found',
            `No nodes found containing "${searchQuery}".`,
            'validation'
          );
        }
        
        // Get all nodes to create document index map
        const allDocContent = reader.readAll();
        const allNodes = allDocContent.nodes;
        
        const nodeIndexMap = new Map<string, number>();
        allNodes.forEach((node: any, idx: number) => {
          nodeIndexMap.set(node.id, idx);
        });
        
        // Enhance nodes with full metadata including document index
        const enhancedNodes = searchResult.nodes.map((node: any) => ({
          id: node.id,
          docIndex: nodeIndexMap.get(node.id) || -1,
          type: node.type,
          wordCount: (node.text || '').split(/\s+/).filter((w: string) => w.trim()).length,
          charCount: node.text?.length || 0,
          text: node.text
        }));
        
        // Apply truncation to fit within 3k word limit
        const MAX_READ_WORDS = 3000;
        const truncationResult = truncateToWordLimit(enhancedNodes, MAX_READ_WORDS, {
          selectorType: 'query',
          searchQuery
        });
        
        const finalNodes = truncationResult.truncatedNodes;
        
        // Convert matching nodes to markdown
        const searchMarkdown = finalNodes.map((node: any) => {
          const text = node.text || '';
          const wordCountStr = node.wordCount ? ` (${node.wordCount} words)` : '';
          return `<!-- ${node.id}${wordCountStr} -->\n${text}`;
        }).join('\n\n');
        
        const resultMessage = truncationResult.truncated
          ? truncationResult.truncationMessage
          : `Found ${finalNodes.length} nodes containing "${searchQuery}"`;
        
        return createSuccessResult(
          resultMessage || `Found ${finalNodes.length} nodes`,
          {
            type: 'search_results',
            markdown: searchMarkdown,
            nodes: finalNodes,
            node_count: finalNodes.length,
            available_node_ids: finalNodes.map((n: any) => n.id),
            searchInfo: {
              query: searchQuery,
              matchCount: searchResult.nodes.length
            },
            metadata: {
              documentName,
              selector: parsedSelector,
              executionTime: Date.now() - startTime
            }
          },
          {
            format: 'json',
            metadata: { 
              executionTime: Date.now() - startTime, 
              documentName, 
              affectedEntities: [resolved.windowId || reader.documentId], 
              method: 'doc_read_v2_search' 
            } as any
          }
        );
      }
      
      // Default: read all
      result = reader.readAll();

      // Debug: Check what reader returned
      log.log('[DOC_READ_V2] 🔍 Reader result analysis:', {
        format: reader.format,
        nodesCount: result.nodes?.length || 0,
        hasMetadata: !!result.metadata,
        resultKeys: Object.keys(result || {}),
        firstNodePreview: result.nodes?.[0] ? {
          id: result.nodes[0].id,
          type: result.nodes[0].type,
          textLength: result.nodes[0].text?.length || 0,
          textPreview: result.nodes[0].text?.substring(0, 100)
        } : null
      });

      // 🚨 FALLBACK: If DocumentReader returns 0 nodes, try LLM Entity Resolver
      // This handles Notes (with markdownExport/base64) and Canvas (with skeleton.elements)
      if (!result.nodes || result.nodes.length === 0) {
        
        try {
          const { llmEntityResolver } = await import('../../../../services/llmEntity/LLMEntityResolver');
          
          // ✅ Use resolve() to fetch full entity with content (base64, markdownExport, etc.)
          // Don't use toLLMRepresentation(entity) because resolved.entity is from cache without content
          const llmRepresentation = await llmEntityResolver.resolve(id);
          
          if (llmRepresentation && llmRepresentation.primaryContent) {
            // Ensure primaryContent is a string (it can be string or object)
            const contentAsString = typeof llmRepresentation.primaryContent === 'string' 
              ? llmRepresentation.primaryContent 
              : JSON.stringify(llmRepresentation.primaryContent);

            // Convert LLM representation to DocumentReader format
            // Create a single node with the content
            const nodeId = `${resolved.entity._id || resolved.entity.id}_content`;
            const contentNode = {
              id: nodeId,
              type: 'paragraph',
              text: contentAsString,
              attributes: {}
            };

            result = {
              nodes: [contentNode],
              text: contentAsString,
              markdown: contentAsString,
              metadata: {
                totalNodes: 1,
                totalWords: contentAsString.split(/\s+/).filter((w: string) => w.trim()).length,
                totalCharacters: contentAsString.length,
                format: llmRepresentation.contentType || 'markdown',
                source: 'llm_entity_resolver',
                ...llmRepresentation.metadata
              }
            };
          } else {
          }
        } catch (resolverError) {
          console.error('[DOC_READ_V2] ❌ LLM Entity Resolver fallback failed:', {
            error: resolverError instanceof Error ? resolverError.message : String(resolverError),
            stack: resolverError instanceof Error ? resolverError.stack : undefined
          });
          // Continue with empty result - will show as "Read 0 nodes"
        }
      }
      
      // Apply truncation to fit within 3k word limit for "all" selector
      const MAX_READ_WORDS = 3000;
      const allNodesWithWordCount = result.nodes?.map((node: any, docIdx: number) => ({
        ...node,
        wordCount: (node.text || '').split(/\s+/).filter((w: string) => w.trim()).length
      })) || [];
      
      const truncationResult = truncateToWordLimit(allNodesWithWordCount, MAX_READ_WORDS, {
        selectorType: 'all'
      });
      
      const finalNodes = truncationResult.truncatedNodes;
      
      log.log('[DOC_READ_V2] All selector processing:', {
        totalNodes: result.nodes?.length || 0,
        totalWords: truncationResult.originalWordCount,
        truncated: truncationResult.truncated,
        returningNodes: finalNodes.length,
        returningWords: truncationResult.totalWords
      });

      // Style context only available for Y.js documents
      // PDFs and read-only docs don't have style context
      // Style context only available for Y.js documents
      // PDFs and read-only docs don't have style context
      const documentStyleContext = {};

      // 🚨 CRITICAL FIX: Create markdown with embedded node IDs for AI targeting
      const markdownWithNodeIds = finalNodes.length > 0 
        ? finalNodes.map((node: any, index: number) => {
            const nodeText = (node.text || '').trim();
            const wordCountStr = node.wordCount ? ` (${node.wordCount} words)` : '';
            return nodeText ? `<!-- ${node.id}${wordCountStr} -->\n${nodeText}` : `<!-- ${node.id}${wordCountStr} -->\n[Empty ${node.type}]`;
          }).join('\n\n')
        : result.markdown || '';

      // Also provide a clean node list for reference with RICH FORMATTING INFO
      const nodeIdList = finalNodes.map((node: any, docIdx: number) => ({
        id: node.id,
        docIndex: docIdx,
        type: node.type,
        wordCount: (node.text || '').split(/\s+/).filter((w: string) => w.trim()).length,
        charCount: (node.text || '').length,
        preview: (node.text || '').substring(0, 100).replace(/\n/g, ' ').trim() || `[Empty ${node.type}]`,
        // ✅ Include rich formatting attributes for AI awareness
        attributes: node.attributes || {},
        hasNestedContent: node.type === 'list_item' && node.attributes?.content,
        isListItem: node.type === 'list_item',
        isOrderedList: node.type === 'ordered_list',
        isBulletList: node.type === 'bullet_list',
        hasSpecialFormatting: !!(node.attributes?.revision_id || node.attributes?.change_status || node.attributes?.style_id)
      })) || [];

      // Use clean markdown with node IDs - no HTML comment pollution
      // List formatting guidance is in the system prompt, not in document reads
      const structureAwareMarkdown = markdownWithNodeIds;

      const allMessage = truncationResult.truncated
        ? truncationResult.truncationMessage
        : `Read ${finalNodes.length} nodes from "${documentName}"`;
      
      const dataPayload = {
        type: 'markdown_content',
        markdown: structureAwareMarkdown,
        node_count: finalNodes.length,  // ✅ Add for formatViewData
        nodeList: nodeIdList,
        originalMarkdown: result.markdown,
        nodes: finalNodes,
        text: result.text,
        styleContext: documentStyleContext,
        // ✅ NEW: Add structural information for AI awareness
        structuralInfo: {
          hasComplexLists: result.nodes?.some((node: any) => 
            node.type === 'ordered_list' || node.type === 'bullet_list'
          ) || false,
          hasNestedLists: result.nodes?.some((node: any) => 
            node.type === 'list_item' && node.attributes?.content
          ) || false,
          listTypes: result.nodes?.filter((node: any) => 
            node.type === 'ordered_list' || node.type === 'bullet_list'
          ).map((node: any) => ({ id: node.id, type: node.type, order: node.attributes?.order })) || [],
          preservationHints: (result.nodes?.some((node: any) => 
            node.type === 'ordered_list' || node.type === 'bullet_list'
          ) || result.nodes?.some((node: any) => 
            node.type === 'list_item' && node.attributes?.content
          )) ? ['preserve_list_structure'] : []
        },
        metadata: { ...(result.metadata || {}), documentName, selector: 'all', executionTime: Date.now() - startTime }
      };
      
      const success = createSuccessResult(
        allMessage || `Read ${documentName} content`,
        dataPayload,
        {
          format: 'json',
          metadata: { executionTime: Date.now() - startTime, documentName, affectedEntities: [resolved.windowId || reader.documentId], method: 'doc_read_v2' } as any
        }
      );
      
      try { context.onProgress?.({ type: 'streaming_update', activity: 'Finalizing...', text_total: result.text?.slice(0, 200) }); } catch {}
      return success;
      
    } catch (error) {
      return createErrorResult('Read operation failed', error instanceof Error ? error.message : 'Unknown error', 'execution', { metadata: { executionTime: Date.now() - startTime } });
    }
  }
};
